import { AbilityBuilder } from '@casl/ability'

import type { Fields as Merchant } from 'models/Merchant'
import type { MerchantPermissions } from 'models/MerchantUser'

type AbilityRule = [string, string] // [action, subject]

type PermissionMapping = {
  view: AbilityRule[]
  manage: AbilityRule[]
}

/**
 * Maps backend permission resource names to frontend CASL (action, subject) pairs.
 * Backend resources come from the `permission_resources` table via the `/me` endpoint.
 * Each resource has view and manage levels that map to one or more CASL rules.
 */
export const PERMISSION_MAP: Record<string, PermissionMapping> = {
  // --- Marketing ---
  // Single `campaigns` resource covers all campaign types (targeted, automated,
  // message). `campaign_collaboration` controls access to other users' campaigns.
  campaigns: {
    view: [
      ['access', 'AllCampaigns'],
      ['access', 'TargetedCampaign'],
      ['access', 'AutomatedCampaign'],
      ['access', 'MessageCampaign'],
      ['access', 'CampaignReports'],
      ['access', 'CampaignPerformanceReport'],
    ],
    manage: [
      ['manage', 'AllCampaigns'],
      ['manage', 'TargetedCampaign'],
      ['manage', 'AutomatedCampaign'],
      ['manage', 'MessageCampaign'],
      ['manage', 'CampaignReports'],
      ['manage', 'CampaignPerformanceReport'],
      ['manage', 'Opportunity'],
      ['manage', 'Results'],
      ['access', 'TargetedCampaign::Custom'],
    ],
  },
  campaign_collaboration: {
    view: [['access', 'OtherUsersCampaigns']],
    manage: [['manage', 'OtherUsersCampaigns']],
  },
  // `campaign_approvals` controls the maker/checker workflow.
  // Without approvals at manage, campaigns require approval before sending.
  campaign_approvals: {
    view: [['access', 'CampaignApprovals']],
    manage: [
      ['review', 'Campaign'],
      ['manage', 'CampaignApprovals'],
    ],
  },
  segments: {
    view: [['access', 'Segments']],
    manage: [['manage', 'Segments']],
  },
  segment_collaboration: {
    view: [['access', 'OtherUsersSegments']],
    manage: [['manage', 'OtherUsersSegments']],
  },
  smart_links: {
    view: [['access', 'SmartLinks']],
    manage: [['manage', 'SmartLinks']],
  },
  promotions: {
    view: [['access', 'Promotions']],
    manage: [['manage', 'Promotions']],
  },

  // --- Customers ---
  feedback: {
    view: [['access', 'Feedback']],
    manage: [['manage', 'Feedback']],
  },
  members: {
    view: [
      ['access', 'Users'],
      ['access', 'UsersByIdentifier'],
      ['access', 'UserLists'],
    ],
    manage: [
      ['manage', 'Users'],
      ['manage', 'UsersByIdentifier'],
      ['manage', 'UserSupport'],
      ['manage', 'UserLists'],
    ],
  },

  // --- Dashboard Management ---
  merchant_users: {
    view: [['access', 'MerchantUsers']],
    manage: [['manage', 'MerchantUsers']],
  },
  billing: {
    view: [['access', 'Billing']],
    manage: [['manage', 'Billing']],
  },
  // Also the gate for RecoveryAI, which had its own `recovery_ai` resource until
  // the two were consolidated — view/edit semantics were identical, so RAI's
  // surfaces now check `MerchantSettings` directly rather than a second subject.
  merchant_settings: {
    view: [['access', 'MerchantSettings']],
    manage: [['manage', 'MerchantSettings']],
  },

  // --- Program Configuration ---
  loyalty: {
    view: [
      ['access', 'Points'],
      ['access', 'Tiers'],
      ['access', 'ExclusiveDeals'],
      ['access', 'LoyaltySetup'],
    ],
    manage: [
      ['manage', 'Points'],
      ['manage', 'Tiers'],
      ['manage', 'ExclusiveDeals'],
      ['manage', 'LoyaltySetup'],
    ],
  },
  reward_templates: {
    view: [['access', 'RedeemManager']],
    manage: [['manage', 'RedeemManager']],
  },
  tiers_program: {
    view: [
      ['access', 'TiersProgram'],
      ['access', 'Tiers'],
    ],
    manage: [
      ['manage', 'TiersProgram'],
      ['manage', 'Tiers'],
    ],
  },
  points_multipliers: {
    view: [
      ['access', 'PointsMultipliers'],
      ['access', 'Points'],
    ],
    manage: [
      ['manage', 'PointsMultipliers'],
      ['manage', 'Points'],
    ],
  },
  reward_issuance: {
    view: [['access', 'RewardIssuance']],
    manage: [['manage', 'RewardIssuance']],
  },
  reward_redemptions: {
    view: [['access', 'RewardRedemptions']],
    manage: [['manage', 'RewardRedemptions']],
  },

  // --- Games ---
  games: {
    view: [['access', 'Games']],
    manage: [['manage', 'Games']],
  },

  // --- Digital Experiences ---
  locations: {
    view: [['access', 'Locations']],
    manage: [['manage', 'Locations']],
  },
  ordering_configuration: {
    view: [['access', 'OrderingManagement']],
    manage: [['manage', 'OrderingManagement']],
  },
  content_management: {
    view: [['access', 'CMS']],
    manage: [['manage', 'CMS']],
  },
  app_configuration: {
    view: [['access', 'AppConfiguration']],
    manage: [['manage', 'AppConfiguration']],
  },
  digital_experiences: {
    view: [['access', 'DigitalExperiences']],
    manage: [['manage', 'DigitalExperiences']],
  },

  // --- Reporting ---
  reporting: {
    view: [['access', 'Reports']],
    manage: [['manage', 'Reports']],
  },
  reporting_location: {
    view: [
      ['access', 'LocationReports'],
      ['access', 'InsightsLab'],
      ['access', 'AnsaReports'],
    ],
    manage: [
      ['manage', 'LocationReports'],
      ['manage', 'InsightsLab'],
      // AnsaReports intentionally stays 'access' even at manage level —
      // the frontend only checks ability.can('access', 'AnsaReports')
      ['access', 'AnsaReports'],
    ],
  },
}

/**
 * Union of every CASL subject used in PERMISSION_MAP.
 * Keep in sync when adding/removing subjects above.
 * (TS 4.7 doesn't support `as const satisfies` — upgrade to 4.9+ to derive automatically.)
 */
export type CaslSubject =
  // Marketing
  | 'AllCampaigns'
  | 'CampaignReports'
  | 'CampaignPerformanceReport'
  | 'AutomatedCampaign'
  | 'MessageCampaign'
  | 'TargetedCampaign'
  | 'Opportunity'
  | 'Results'
  | 'TargetedCampaign::Custom'
  | 'Campaign'
  | 'CampaignApprovals'
  | 'OtherUsersCampaigns'
  | 'Segments'
  | 'OtherUsersSegments'
  | 'SmartLinks'
  // Customers
  | 'Feedback'
  | 'Users'
  | 'UsersByIdentifier'
  | 'UserLists'
  | 'UserSupport'
  // Dashboard Management
  | 'MerchantUsers'
  | 'Billing'
  | 'MerchantSettings'
  // Program Configuration
  | 'Points'
  | 'PointsMultipliers'
  | 'Tiers'
  | 'TiersProgram'
  | 'ExclusiveDeals'
  | 'LoyaltySetup'
  | 'RedeemManager'
  | 'RewardIssuance'
  | 'RewardRedemptions'
  // Games
  | 'Games'
  // Digital Experiences
  | 'Locations'
  | 'OrderingManagement'
  | 'CMS'
  | 'AppConfiguration'
  | 'DigitalExperiences'
  // Reporting
  | 'Reports'
  | 'LocationReports'
  | 'InsightsLab'
  | 'AnsaReports'
  // Other
  | 'Integrations'
  | 'Promotions'

/**
 * Reverse lookup: CASL subject → backend resource name.
 * Built once at module load from PERMISSION_MAP.
 * When a subject appears in multiple resources, the first match wins
 * (the broader resource is listed first in the map).
 */
export const SUBJECT_TO_RESOURCE: Record<string, string> = (() => {
  const map: Record<string, string> = {}
  for (const [resource, mapping] of Object.entries(PERMISSION_MAP)) {
    for (const [, subject] of [...mapping.view, ...mapping.manage]) {
      if (!map[subject]) map[subject] = resource
    }
  }
  return map
})()

/**
 * Look up the display name for a CASL subject from grouped permissions data.
 * Returns the backend display_name (e.g. "Location Reports") or a
 * humanized fallback from the resource slug (e.g. "reporting location").
 */
export const findPermissionDisplayName = (
  subject: CaslSubject,
  groups: { resources: { name: string; display_name: string }[] }[] | undefined
): string | undefined => {
  const resourceName = SUBJECT_TO_RESOURCE[subject]
  if (!resourceName || !groups) return undefined

  for (const group of groups) {
    const resource = group.resources.find(r => r.name === resourceName)
    if (resource) return resource.display_name
  }
  return undefined
}

export const getPermissionLabel = (
  subject: CaslSubject,
  groups: { resources: { name: string; display_name: string }[] }[] | undefined
): string | undefined => {
  const resourceName = SUBJECT_TO_RESOURCE[subject]
  if (!resourceName) return undefined

  // Fallback: humanize the resource slug. Acceptable inside a tooltip, which is
  // supplementary — NOT in body copy, where "reporting location" reads as a
  // typo. Callers rendering prose want findPermissionDisplayName above, so an
  // unresolved payload yields undefined and their own curated copy takes over.
  return (
    findPermissionDisplayName(subject, groups) ??
    resourceName.replace(/_/g, ' ')
  )
}

/**
 * Builds a CASL Ability instance from backend RBAC permission data.
 * Used when `role-based-access-control` flag is enabled and the user has
 * RBAC roles assigned (non-empty permissions map).
 */
export const defineAbilitiesFromPermissions = (
  permissions: MerchantPermissions,
  merchant: Merchant | null
) => {
  return AbilityBuilder.define(
    (
      can: (action: string, subject: string) => void,
      cannot: (action: string, subject: string) => void
    ) => {
      const entries = Object.entries(permissions)

      if (!entries.length) {
        can('manage', 'Login')
        return
      }

      let rulesGranted = 0

      entries.forEach(([resourceName, actions]) => {
        const mapping = PERMISSION_MAP[resourceName]
        if (!mapping) return // Unknown resource — skip

        if (actions.includes('view')) {
          mapping.view.forEach(([action, subject]) => can(action, subject))
          rulesGranted++
        }
        if (actions.includes('manage')) {
          // manage implies view
          mapping.view.forEach(([action, subject]) => can(action, subject))
          mapping.manage.forEach(([action, subject]) => can(action, subject))
          rulesGranted++
        }
      })

      // Safety net: if all resources were unmapped, still allow login
      if (rulesGranted === 0) {
        can('manage', 'Login')
      }

      // Merchant-type overrides (same as legacy path)
      // Location targeting abilities (targetCategorizedLocations, targetAllLocations,
      // TargetedCampaign::Location) are intentionally omitted here — under RBAC,
      // location targeting is driven by the user's location_ids, not CASL abilities.
      if (merchant?.merchant_type === 'Mall') {
        cannot('access', 'Feedback')
        cannot('access', 'OperationalReports')
        cannot('access', 'AverageCheckReport')
        cannot('access', 'PurchasesReport')
        cannot('access', 'InsightsLab')
      }
    }
  )
}
